import React, { useState, useEffect, useMemo } from 'react';
import { When } from 'react-if';
import { useSelector } from 'react-redux';
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

import Loading from '../../loading';
import NoData from '../../no-data';
import { RootState } from '../../../store';
import { PaginationFactory, PaginatorFactory } from '../../../factory';
import { MobileAppUserService } from '../../../services/mobile-app-user.service';
import { MobileAppUserTypes } from '../../../types';
import { MOBILE_APP_USER_COLUMNS } from '../../../builders/mobile-app-users-builders';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import OptionsDrawer, { DrawerOption } from '../../options-drawer';
import { MobileAppUsersFilters } from './mobile-app-users-filters';
import { MobileAppUserDetailDrawer } from './mobile-app-user-detail-drawer';
import { formatOsVersion } from './os-version';

export const MobileAppUsersList: React.FC = React.memo(() => {
    const [optionsDrawer, setOptionsDrawer] = useState<boolean>(false);
    const [detailDrawer, setDetailDrawer] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState({} as MobileAppUserTypes.Model);
    const [options, setOptions] = useState<DrawerOption[]>([]);
    const [customers, setCustomers] = useState<MobileAppUserTypes.CustomerForFilter[]>([]);
    const [currentFilters, setCurrentFilters] = useState<MobileAppUserTypes.Filters>({ search: '', appStatus: undefined, appVersion: '', osVersion: '' });

    const { pagination: { total, items }, loading } = useSelector((state: RootState) => state.mobileAppUser);

    const [pagination, setPagination] = useState<PaginatorFactory<any>>({
        page: 0,
        perPage: 50, // Increased default page size for better performance
    });

    const onFilter = async (filters: MobileAppUserTypes.Filters) => {
        // Store current filters to preserve them across pagination
        setCurrentFilters(filters);
        // Reset to first page when applying filters
        setPagination(prev => ({ ...prev, page: 0 }));
        await MobileAppUserService.fetchMany({ 
            ...pagination, 
            page: 1, // Always go to first page when filtering
            filter: { ...filters } 
        });
    };

    const makeCsvDownload = async () => {
        const res: PaginationFactory<MobileAppUserTypes.Model> = await MobileAppUserService.fetchMany(
            { perPage: 100000, page: 1, filter: { ...currentFilters } },
            undefined,
            true,
        ) as PaginationFactory<MobileAppUserTypes.Model>;

        const csvArray = res.items?.map((user) => ({
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            phoneNumber: user.phoneNumber || '',
            customerName: getCustomerName(user.subscriptionId),
            appStatus: user.appStatus || '',
            appVersion: user.appVersion || '',
            osVersion: user.osVersion ? formatOsVersion(user.platform, user.osVersion) : '',
            employeeId: user.employeeId || '',
            backupEmail: user.backupEmail || '',
            createdAt: user.createdAt ? dayjs(user.createdAt).format('MM/DD/YYYY hh:mm A') : '',
            updatedAt: user.updatedAt ? dayjs(user.updatedAt).format('MM/DD/YYYY hh:mm A') : '',
        }));

        downloadCsv(csvArray, 'mobile-app-users');
    };

    // Function to lookup customer name by subscriptionId
    const getCustomerName = useMemo(() => {
        return (subscriptionId: string): string => {
            const customer = customers.find(c => c.subscriptionId === subscriptionId);
            return customer?.name || 'Unknown Customer';
        };
    }, [customers]);

    // Enhanced user data with computed fields (memoized for performance)
    const enhancedUsers = useMemo(() => {
        return items?.map((user) => ({
            ...user,
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A',
            // Lookup customer name from customers list using subscriptionId
            customerName: getCustomerName(user.subscriptionId),
        })) || [];
    }, [items, getCustomerName]);

    const getAppStatusStyle = (status?: string) => {
        switch (status) {
            case 'ON':
                return {
                    backgroundColor: '#4caf50',
                    color: '#ffffff'
                };
            case 'OFF':
                return {
                    backgroundColor: '#f44336',
                    color: '#ffffff'
                };
            default:
                return {
                    backgroundColor: '#9e9e9e',
                    color: '#ffffff'
                };
        }
    };

    const StatusChip = ({ label }: { label: string }) => {
        const statusStyle = getAppStatusStyle(label);
        return (
            <Box 
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    border: '1px solid',
                    borderColor: 'rgba(0,0,0,0.12)',
                    ...statusStyle
                }}
            >
                {label || 'Unknown'}
            </Box>
        );
    };

    useEffect(() => {
        MobileAppUserService.fetchMany({ 
            ...pagination, 
            page: pagination.page + 1, 
            filter: { ...currentFilters } 
        });
    }, [pagination?.perPage, pagination?.page, currentFilters]);

    // Load customers list for customer name lookup
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const customerData = await MobileAppUserService.getCustomersForFilter();
                setCustomers(customerData);
            } catch (error) {
                console.error('Failed to load customers:', error);
                setCustomers([]);
            }
        };
        
        fetchCustomers();
    }, []);

    useEffect(() => {
        const opts = [
            {
                icon: <ManageSearchIcon />,
                label: 'View User Details',
                onClick: () => {
                    setOptionsDrawer(false);
                    setDetailDrawer(true);
                }
            }
        ];

        setOptions(opts);
    }, [selectedUser]);

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <MobileAppUsersFilters 
                    onFilter={onFilter}
                    onDownload={makeCsvDownload}
                />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {MOBILE_APP_USER_COLUMNS?.map((column, index) => (
                                    <TableCell key={index}>
                                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                            {column?.headerName}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <When condition={!loading?.fetchMany}>
                            <TableBody>
                                {enhancedUsers?.map((user) => (
                                    <TableRow 
                                        key={user._id} 
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setDetailDrawer(true);
                                        }}
                                        sx={{ 
                                            ":hover": { 
                                                backgroundColor: '#F9FAFB',
                                                cursor: 'pointer'
                                            }
                                        }} 
                                    >
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                                    {user.fullName}
                                                </Typography>
                                                {user.employeeId && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        ID: {user.employeeId}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        
                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {user.phoneNumber || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {user.customerName}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <StatusChip label={user.appStatus || 'Unknown'} />
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {user.appVersion || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {formatOsVersion(user.platform, user.osVersion)}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {user.createdAt ? dayjs(user.createdAt).format('MM/DD/YY') : 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {user.updatedAt ? dayjs(user.updatedAt).format('MM/DD/YY') : 'N/A'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </When>
                    </Table>

                    <When condition={loading?.fetchMany}>
                        <Loading />
                    </When>

                    <When condition={loading.fetchMany === false && items?.length < 1}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={'No mobile app users found'}
                            description={'No mobile app users match your current search and filter criteria.'}
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={total ?? 0}
                    rowsPerPage={pagination?.perPage}
                    page={pagination?.page}
                    onPageChange={(e, p) => setPagination(pag => ({ ...pag, page: p }))}
                    onRowsPerPageChange={(e) => setPagination(pag => ({ ...pag, perPage: parseInt(e.target.value, 10), page: 0 }))}
                />
            </Card>

            <OptionsDrawer 
                anchor="bottom"
                onClose={() => {
                    setOptionsDrawer(false);
                }}
                open={optionsDrawer}
                options={options}
            />

            <MobileAppUserDetailDrawer
                user={selectedUser}
                visible={detailDrawer}
                onClose={() => {
                    setDetailDrawer(false);
                    setSelectedUser({} as MobileAppUserTypes.Model);
                }}
            />
        </>
    );
});
